import React from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import './App.css';
import Layout from './components/Layout';
import IssueList from './components/IssueList';
import ReportForm from './components/ReportForm';
import DashboardPage from './pages/DashboardPage';
import SettingsPage from './pages/SettingsPage';
import ChecklistFormPage from './pages/ChecklistFormPage';
import LoginPage from './pages/LoginPage';
import SignUpPage from './pages/SignUpPage';
import PrivateRoute from './components/PrivateRoute';
import FacilityListPage from './pages/FacilityListPage';
import FacilityFormPage from './pages/FacilityFormPage';
import FacilityDetailPage from './pages/FacilityDetailPage';
import SectionFormPage from './pages/SectionFormPage';
import FacilityManagePage from './pages/FacilityManagePage';
import InspectionPage from './pages/InspectionPage';
import WorkerListPage from './pages/WorkerListPage';
import GlobalModal from './components/GlobalModal';
import PushNotificationService from './services/PushNotificationService';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import InspectionDetailPage from './pages/InspectionDetailPage';
import IssueDetailPage from './pages/IssueDetailPage';
import ChecklistManagementPage from './pages/ChecklistManagementPage';
import StatisticsPage from './pages/StatisticsPage';
import InspectionMatrixPage from './pages/InspectionMatrixPage';
import TodayPage from './pages/TodayPage';
import RoundDetailPage from './pages/RoundDetailPage';
import DeletedReportsPage from './pages/DeletedReportsPage';
import UnitsPage from './pages/UnitsPage';
import LeaseListPage from './pages/LeaseListPage';
import InviteAcceptPage from './pages/InviteAcceptPage';
import PasswordResetPage from './pages/PasswordResetPage';
import ClientListPage from './pages/ClientListPage';
import LeaseFormPage from './pages/LeaseFormPage';
import HelpPage from './pages/HelpPage';
import { FirebaseAnalytics } from '@capacitor-firebase/analytics';
import { initializeApp } from 'firebase/app';
import { SafeArea } from 'capacitor-plugin-safe-area';
import { App as CapacitorApp } from '@capacitor/app';
import { StatusBar, Style } from '@capacitor/status-bar';
import InspectionPlanListPage from './pages/InspectionPlanListPage';
import InspectionPlanFormPage from './pages/InspectionPlanFormPage';

/**
 * 정거장이 바뀌면 화면을 새로 태운다.
 *
 * 한 회차에서 다음 곳으로 이어 갈 때 같은 주소·같은 화면으로 이동하므로,
 * 그냥 두면 앞 정거장의 체크와 사진과 메모가 그대로 남는다. 지울 상태를 하나씩
 * 세는 것보다 통째로 다시 세우는 편이 빠뜨릴 데가 없다.
 */
const KeyedInspectionPage = () => {
  const location = useLocation();
  return <InspectionPage key={location.search} />;
};

// Firebase configuration for web
const firebaseConfig = {
  apiKey: "AIzaSyBNQKW--bDqrz8zIywaxqUe3i1VEolTNFQ",
  authDomain: "barobogo.firebaseapp.com",
  projectId: "barobogo",
  storageBucket: "barobogo.firebasestorage.app",
  messagingSenderId: "454495073977",
  appId: "1:454495073977:web:44783e75e5cba8930a8d68",
  measurementId: "G-KB7KZYFRN7"
};

// Initialize Firebase for web if not already initialized
try {
  initializeApp(firebaseConfig);
} catch (e) {
  console.error("Firebase App initialization error:", e);
}

/**
 * 알림 탭은 Router 밖에서 들어오므로 곧바로 화면을 옮길 수 없다. 이 컴포넌트가
 * Router 안에서 그 값을 받아 이동시킨다. 전체 리로드가 아니라 라우터 이동이라
 * 하던 점검이 있어도 살아남는다.
 *
 * 마운트할 때 한 번 확인하는 것은, 앱이 꺼져 있다가 알림으로 열리는 경우 탭이
 * 이 컴포넌트보다 먼저 도착하기 때문이다.
 */
const PushNavigationBridge = () => {
  const navigate = useNavigate();

  React.useEffect(() => {
    const go = () => {
      const path = PushNotificationService.takePendingPath();
      if (path) navigate(path);
    };
    go();
    window.addEventListener(PushNotificationService.NAVIGATE_EVENT, go);
    return () => window.removeEventListener(PushNotificationService.NAVIGATE_EVENT, go);
  }, [navigate]);

  return null;
};

function App() {
  React.useEffect(() => {
    // Initialize Firebase Analytics
    const initAnalytics = async () => {
      try {
        await FirebaseAnalytics.logEvent({ name: 'app_open' });
      } catch (e) {
        console.error('Firebase Analytics Error:', e);
      }
    };
    initAnalytics();

    // Initialize Safe Area CSS Variables for Capacitor Android
    const initSafeArea = async () => {
      try {
        // Android 15+ ignores setBackgroundColor: enforced edge-to-edge keeps the
        // status bar transparent, showing whatever sits beneath it -- here the
        // app's light header. Style.Dark paints white icons onto that, which is
        // why the clock and battery disappeared, so the bar gets dark icons.
        await StatusBar.setStyle({ style: Style.Light });

        const { insets } = await SafeArea.getSafeAreaInsets();
        for (const [key, value] of Object.entries(insets)) {
          document.documentElement.style.setProperty(
            `--safe-area-inset-${key}`,
            `${value}px`,
          );
        }

        // Listen for safe area changes (e.g., orientation change)
        SafeArea.addListener('safeAreaChanged', data => {
          const { insets } = data;
          for (const [key, value] of Object.entries(insets)) {
            document.documentElement.style.setProperty(
              `--safe-area-inset-${key}`,
              `${value}px`,
            );
          }
        });
      } catch (e) {
        console.log('SafeArea plugin not available or error:', e);
      }
    };
    initSafeArea();

    // Hardware Back Button Handler
    let backButtonListener;
    const initBackButton = async () => {
      backButtonListener = await CapacitorApp.addListener('backButton', ({ canGoBack }) => {
        if (canGoBack || window.history.length > 1) {
          // Attempt to go back if history exists
          // If we are on the root dashboard or login, exit the app
          if (window.location.pathname === '/' || window.location.pathname === '/login') {
            CapacitorApp.exitApp();
          } else {
            window.history.back();
          }
        } else {
          CapacitorApp.exitApp();
        }
      });
    };
    initBackButton();

    // Early initialization of Push Notifications
    // This ensures the app is recognized by the OS as a notification-capable app
    PushNotificationService.init().catch(err => console.error('Push early init error:', err));


    return () => {
      if (backButtonListener) {
        backButtonListener.remove();
      }
    };
  }, []);

  return (
    <Router>
      <PushNavigationBridge />
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        {/* 회사를 새로 세우는 문. 이미 있는 회사에 들어가는 길은 초대뿐이다. */}
        <Route path="/signup" element={<SignUpPage />} />
        {/* 아직 계정이 없는 사람이 여는 곳이라 로그인 밖에 둔다. */}
        <Route path="/invite/:token" element={<InviteAcceptPage />} />
        <Route path="/reset/:token" element={<PasswordResetPage />} />

        {/* Routes with Main Layout */}
        <Route element={<Layout />}>
          <Route element={<PrivateRoute />}>
            <Route path="/" element={<DashboardPage />} />
            <Route path="/reports" element={<IssueList />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/help" element={<HelpPage />} />
            <Route path="/checklists/new" element={<ChecklistFormPage />} />
            <Route path="/checklists/:id" element={<ChecklistFormPage />} />
            <Route path="/new" element={<ReportForm />} />
            <Route path="/reports/:id" element={<InspectionDetailPage />} />
            {/* 정적인 쪽을 먼저 둔다. 라우터가 알아서 가려내기는 하지만,
                읽는 사람이 :id 에 걸릴까 의심하지 않아도 되게. */}
            <Route path="/inspections/deleted" element={<DeletedReportsPage />} />
            <Route path="/inspections/:id" element={<InspectionDetailPage />} />
            <Route path="/issues/:id" element={<IssueDetailPage />} />
            <Route path="/facilities" element={<FacilityListPage />} />
            <Route path="/facilities/new" element={<FacilityFormPage />} />
            <Route path="/facilities/:id" element={<FacilityDetailPage />} />
            <Route path="/facilities/:id/update" element={<FacilityFormPage />} />
            <Route path="/facilities/:id/edit" element={<FacilityManagePage />} />
            <Route path="/facilities/:facilityId/sections/new" element={<SectionFormPage />} />
            <Route path="/facilities/:facilityId/sections/:sectionId/edit" element={<SectionFormPage />} />
            <Route path="/checklist-management" element={<ChecklistManagementPage />} />
            <Route path="/today" element={<TodayPage />} />
            <Route path="/inspection-matrix" element={<InspectionMatrixPage />} />
            <Route path="/rounds/:slotId" element={<RoundDetailPage />} />
            <Route path="/facilities/:id/units" element={<UnitsPage />} />
            <Route path="/leases" element={<LeaseListPage />} />
            <Route path="/clients" element={<ClientListPage />} />
            {/* 정적인 쪽을 :id 보다 먼저. */}
            <Route path="/leases/new" element={<LeaseFormPage />} />
            <Route path="/leases/:id" element={<LeaseFormPage />} />
            <Route path="/inspection-plans" element={<InspectionPlanListPage />} />
            <Route path="/inspection-plans/new" element={<InspectionPlanFormPage />} />
            <Route path="/inspection-plans/:id" element={<InspectionPlanFormPage />} />
            <Route path="/statistics" element={<StatisticsPage />} />
            <Route path="/workers" element={<WorkerListPage />} />
          </Route>
        </Route>

        {/* Standalone Routes (No Layout) - Auth handled internally */}
        <Route path="/facilities/:id/inspect" element={<KeyedInspectionPage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
      </Routes>
      <GlobalModal />
    </Router>
  );
}

export default App;
